import React, { useEffect, useMemo, useRef, useState, useCallback } from 'react';
import { Link, useLocation, useParams } from 'react-router-dom';
import toast from 'react-hot-toast';
import { ArrowLeft, Loader2, Play } from 'lucide-react';
import { BeachvisionLayout } from '../../components/beachvision/BeachvisionLayout';
import { DebugDrawer } from '../../components/beachvision/DebugDrawer';
import { DebugPanel } from '../../components/beachvision/DebugPanel';
import { DrillCard } from '../../components/beachvision/DrillCard';
import { PageErrorBoundary } from '../../components/beachvision/PageErrorBoundary';
import { formatScore } from '../../lib/beachvision-utils';
import { getAnalysisTakeawayDetail } from '../../api/beachvision';
import { isDebugModeEnabled } from '../../lib/debug-mode';
import { buildQualityCheckDebugItems } from '../../lib/quality-check-debug';
import { useAnalysis } from '../../hooks/useAnalysis';

/**
 * Takeaway detail with inline video playback, form score panel, and drill list.
 */
function analysisTakeawayListPath(analysisId, pathname) {
  const base = `/analysis/${analysisId}/`;
  if ((pathname || '').includes('/strengths/')) {
    return `${base}strengths/`;
  }
  return `${base}takeaways/`;
}

function TakeawayDetailInner({ currentUser }) {
  const { id, tid } = useParams();
  const location = useLocation();
  const listPath = analysisTakeawayListPath(id, location.pathname);
  const [takeaway, setTakeaway] = useState(null);
  const [detailLoading, setDetailLoading] = useState(true);
  const [detailError, setDetailError] = useState(false);
  const [reloadCount, setReloadCount] = useState(0);
  const [clips, setClips] = useState([]);
  const [clipUrl, setClipUrl] = useState(null);
  const [selectedPointId, setSelectedPointId] = useState(null);
  const [pendingSeekSeconds, setPendingSeekSeconds] = useState(null);
  const [clipLoading, setClipLoading] = useState(true);
  const [clipError, setClipError] = useState(false);
  const videoRef = useRef(null);

  const debugModeEnabled = isDebugModeEnabled(currentUser);

  const { data: analysisForDebug } = useAnalysis(id, {
    enabled: Boolean(id) && debugModeEnabled,
    refetchWhileProcessingMs: 4000,
  });

  const qualityCheckDebugItems = useMemo(() => {
    const raw =
      analysisForDebug?.ok && analysisForDebug.data
        ? analysisForDebug.data.raw_gemini_response
        : null;
    return buildQualityCheckDebugItems(raw);
  }, [analysisForDebug]);

  useEffect(() => {
    if (!id || !tid) return;
    let cancelled = false;
    setDetailLoading(true);
    setDetailError(false);
    getAnalysisTakeawayDetail(id, tid)
      .then((payload) => {
        if (cancelled) return;
        setTakeaway(payload || null);
      })
      .catch(() => {
        if (!cancelled) {
          setTakeaway(null);
          setDetailError(true);
        }
      })
      .finally(() => {
        if (!cancelled) setDetailLoading(false);
      });
    return () => { cancelled = true; };
  }, [id, tid, reloadCount]);

  const formatClipChipTime = (seekSeconds) => {
    const totalSeconds = Math.max(0, Math.floor(Number(seekSeconds) || 0));
    const minutes = Math.floor(totalSeconds / 60);
    const seconds = String(totalSeconds % 60).padStart(2, '0');
    return `${minutes}:${seconds}s`;
  };

  const loadPointClipForTakeaway = useCallback((pointClip) => {
    if (!pointClip?.clip_video_url) {
      setClipError(true);
      setClipLoading(false);
      return Promise.resolve();
    }
    const seekSeconds = Math.max(0, Number(pointClip.time_in_point_seconds) || 0);
    setClipLoading(true);
    setClipError(false);
    return Promise.resolve()
      .then(() => {
        setClipUrl(pointClip.clip_video_url || null);
        setSelectedPointId(pointClip.point_id);
        setPendingSeekSeconds(seekSeconds);
      })
      .finally(() => {
        setClipLoading(false);
      });
  }, []);

  useEffect(() => {
    if (!takeaway) return;
    const ordered = [...(takeaway.point_takeaways || [])].sort(
      (a, b) => (a.point_order || 0) - (b.point_order || 0)
    );
    setClips(ordered);
    setClipError(false);
    setClipUrl(null);
    setPendingSeekSeconds(null);
    if (!ordered.length) {
      setClipLoading(false);
      setSelectedPointId(null);
      return;
    }
    loadPointClipForTakeaway(ordered[0]);
  }, [loadPointClipForTakeaway, takeaway]);

  useEffect(() => {
    if (!clipUrl || pendingSeekSeconds == null) return;
    const video = videoRef.current;
    if (!video) return;
    const seekToPendingTime = () => {
      const bounded = Math.max(0, pendingSeekSeconds);
      video.currentTime = bounded;
      // Keep playback moving from the selected takeaway timestamp.
      video.play().catch(() => {});
      setPendingSeekSeconds(null);
    };
    if (video.readyState >= 1) {
      seekToPendingTime();
      return undefined;
    }
    video.addEventListener('loadedmetadata', seekToPendingTime, { once: true });
    return () => {
      video.removeEventListener('loadedmetadata', seekToPendingTime);
    };
  }, [clipUrl, pendingSeekSeconds]);

  if (detailLoading) {
    return (
      <div className="px-4 py-16 text-center flex flex-col items-center gap-3">
        <Loader2 className="h-8 w-8 text-beachvision-teal animate-spin" />
        <p className="text-beachvision-muted">Loading takeaway...</p>
      </div>
    );
  }

  if (!takeaway && detailError) {
    return (
      <div className="px-4 py-16 text-center">
        <p className="text-beachvision-muted mb-4">
          Takeaway details are not available yet.
        </p>
        <button
          type="button"
          onClick={() => setReloadCount((value) => value + 1)}
          className="w-full max-w-xs mx-auto bg-beachvision-cta text-beachvision-on-coral rounded-xl px-6 py-3 font-display font-bold mb-3"
        >
          Retry
        </button>
        <Link
          to={listPath}
          className="text-beachvision-coral font-display font-bold"
        >
          Go to analysis
        </Link>
      </div>
    );
  }

  if (!takeaway) {
    return (
      <div className="px-4 py-16 text-center">
        <p className="text-beachvision-muted mb-4">
          This takeaway could not be found.
        </p>
        <Link
          to={listPath}
          className="text-beachvision-coral font-display font-bold"
        >
          {(location.pathname || '').includes('/strengths/')
            ? 'Back to strengths'
            : 'Back to takeaways'}
        </Link>
      </div>
    );
  }

  const drills = [...(takeaway.drill_recommendations || [])].sort(
    (a, b) => a.order - b.order
  );
  const selectedClip =
    clips.find((clip) => String(clip.point_id) === String(selectedPointId)) || clips[0] || null;

  const takeawayDebugItems = [
    { label: 'Session ID', value: String(id || '-') },
    { label: 'Takeaway ID', value: String(takeaway?.id || '-') },
    { label: 'Category', value: String(takeaway?.category || '-') },
    { label: 'Takeaway score', value: String(takeaway?.score ?? '-') },
    {
      label: 'Time window',
      value: `${takeaway?.timestamp_start_ms ?? '-'}ms -> ${takeaway?.timestamp_end_ms ?? '-'}ms`,
    },
    { label: 'Takeaway best shot', value: String(takeaway?.best_shot_timestamp_ms ?? '-') },
    { label: 'Point clips', value: String((takeaway?.point_takeaways || []).length) },
    { label: 'Drill count', value: String(drills.length) },
    { label: 'Clip source', value: clipUrl ? 'point-clip' : clipLoading ? 'loading' : 'none' },
  ];
  const pointClipDebugItems = [
    { label: 'Selected point', value: String(selectedPointId || 'none') },
    {
      label: 'Clip record',
      value: selectedClip ? `point_${selectedClip.point_id}` : 'not available',
    },
    { label: 'Point order', value: String(selectedClip?.point_order ?? '-') },
    { label: 'Match score', value: String(selectedClip?.score ?? '-') },
    { label: 'Best frame ms', value: String(selectedClip?.best_frame_ms ?? '-') },
    {
      label: 'Clip window',
      value: selectedClip
        ? `${selectedClip.clip_start_ms}ms -> ${selectedClip.clip_end_ms}ms`
        : '-',
    },
    {
      label: 'Seek offset',
      value: selectedClip ? `${selectedClip?.time_in_point_seconds ?? '-'}s` : '-',
    },
    { label: 'Pending seek', value: `${pendingSeekSeconds ?? '-'}s` },
  ];

  return (
    <>
      <header className="px-4 pt-6 flex items-center gap-3">
        <Link
          to={listPath}
          className="p-2.5 rounded-xl bg-beachvision-surface-low text-white"
          aria-label="Back to analysis"
        >
          <ArrowLeft className="h-5 w-5" />
        </Link>
        <div className="flex-1 min-w-0">
          <h1 className="font-display font-black text-white uppercase text-sm tracking-wide truncate leading-tight">
            {takeaway.title}
          </h1>
        </div>
      </header>

      <div className="px-4 mt-4">
        <div className="relative rounded-2xl overflow-hidden bg-beachvision-surface-low min-h-56">
          {clipLoading && (
            <div className="w-full h-56 flex flex-col items-center justify-center gap-2">
              <Loader2 className="h-8 w-8 text-beachvision-teal animate-spin" />
              <p className="text-xs text-beachvision-muted font-display font-bold uppercase tracking-wide">
                Loading clip…
              </p>
            </div>
          )}

          {!clipLoading && clipError && (
            <div className="w-full h-56 flex flex-col items-center justify-center gap-2">
              <Play className="h-8 w-8 text-beachvision-muted" />
              <p className="text-xs text-beachvision-muted font-display font-bold uppercase tracking-wide">
                Clip unavailable
              </p>
            </div>
          )}

          {!clipLoading && clipUrl && (
            <video
              ref={videoRef}
              src={clipUrl}
              className="w-full h-56 object-contain bg-black"
              controls
              autoPlay
              playsInline
              loop
            />
          )}

          {takeaway.annotation_label && !clipLoading && clipUrl && (
            <span className="absolute bottom-3 left-1/2 -translate-x-1/2 rounded-full bg-beachvision-teal/90 px-3 py-1 text-xs font-display font-bold text-beachvision-bg uppercase tracking-wide backdrop-blur-sm whitespace-nowrap">
              {takeaway.annotation_label}
            </span>
          )}

          <div className="absolute top-3 right-3 rounded-xl bg-black/55 backdrop-blur-md px-3 py-2 text-right">
            <p className="text-2xl font-display font-black text-beachvision-teal leading-none">
              {formatScore(takeaway.analysis_form_consistency_pct)}
            </p>
            <p className="text-xs text-beachvision-muted font-display font-bold uppercase tracking-wide mt-1">
              Form score
            </p>
          </div>
        </div>

        {clips.length > 1 ? (
          <div className="mt-3 flex gap-2 overflow-x-auto pb-1">
            {clips.map((c) => (
              <button
                key={`${c.point_id}-${c.clip_start_ms}`}
                type="button"
                onClick={() => loadPointClipForTakeaway(c)}
                className={`shrink-0 rounded-full px-3 py-1.5 text-left text-xs font-display font-bold uppercase tracking-wide ${
                  selectedPointId === c.point_id
                    ? 'bg-beachvision-coral text-beachvision-on-coral'
                    : 'bg-beachvision-surface-low text-beachvision-muted'
                }`}
              >
                <span className="block leading-none">Point {c.point_order}</span>
                <span className="block mt-1 text-[10px] font-semibold normal-case opacity-65 leading-none">
                  {formatClipChipTime(
                    c.time_in_point_seconds
                  )}
                </span>
              </button>
            ))}
          </div>
        ) : null}

        <h2 className="font-display font-bold text-xl text-white mt-6">
          {takeaway.title}
        </h2>
        <p className="text-beachvision-muted mt-3 leading-relaxed text-sm">
          {takeaway.description}
        </p>

        <section className="mt-10 mb-4">
          <h3 className="font-display font-bold text-white text-sm uppercase tracking-wide mb-3">
            Recommended drills
          </h3>
          <div className="space-y-3">
            {drills.length === 0 ? (
              <div className="rounded-xl border border-white/5 bg-beachvision-surface-low p-4 text-center">
                <p className="text-sm text-beachvision-muted">
                  Drill recommendations will be coming soon.
                </p>
              </div>
            ) : (
              drills.map((d) => (
                <DrillCard key={d.id} drill={d} />
              ))
            )}
          </div>
        </section>

        <DebugDrawer enabled={debugModeEnabled}>
          <DebugPanel title="Takeaway Debug" items={takeawayDebugItems} />
          <DebugPanel title="TakeawayPointClip Debug" items={pointClipDebugItems} />
          <DebugPanel title="Quality check (intake)" items={qualityCheckDebugItems} />
        </DebugDrawer>
      </div>
    </>
  );
}

export default function TakeawayDetailPage(props) {
  return (
    <BeachvisionLayout>
      <PageErrorBoundary>
        <TakeawayDetailInner {...props} />
      </PageErrorBoundary>
    </BeachvisionLayout>
  );
}
